'use client';
import { use, useEffect, useRef } from 'react';
import { elevarBaseData, elevarViewCart } from '@/lib/elevar/cart-events';
import { elevarEnsureContext } from '@/lib/elevar/ensure-context';
import { elevarPageTypePage } from '@/lib/elevar/page-type';
import { useCart } from '@/contexts/CartContext';
import { usePricing } from '@/contexts/PricingContext';

/**
 * Fires cart page events per Elevar headless docs: `dl_user_data` (base event on every
 * virtual page change) then `dl_view_cart` (via `elevarViewCart`; empty cart sends `impressions: []`, `cart_total: "0.0"`),
 * then the legacy `pageType: 'cart'` push.
 * `cartFromServer` hydrates until client cart context loads.
 * Mount at route level (no `key`) so base data fires even if CMS cart blocks are missing.
 * Once `hasFiredRef` is marked, the sequence is committed to complete,
 * preventing dropped secondary events if dependencies update mid-flight.
 */
export default function ElevarCart({ cartFromServer }) {
  const { cart: cartFromContext } = useCart();
  const pricing = usePricing();
  // Conditional on purpose: use() unconditionally here would suspend on every
  // render, delaying the Elevar events this component exists to fire.
  const mergedCart = cartFromContext === null ? use(cartFromServer) : cartFromContext;
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (mergedCart === null || hasFiredRef.current) return;

    let cancelled = false;

    (async () => {
      await elevarEnsureContext();
      if (cancelled) return;

      hasFiredRef.current = true;
      await elevarBaseData(mergedCart, pricing);

      await elevarViewCart(mergedCart, pricing);
      elevarPageTypePage('cart');
    })();

    return () => { cancelled = true; };
  }, [mergedCart]);

  return null;
}
